/**
 * Self-hosted implementation of DatabaseAdapter.
 * Calls a REST API backed by direct PostgreSQL.
 *
 * Base URL is read from NEXT_PUBLIC_SELF_HOSTED_API_URL.
 * All requests carry a JWT from SelfHostedAuthAdapter.
 */

import type { DatabaseAdapter, QueryFilter, QueryOptions, UpsertOptions } from '../interfaces';

const BASE = () =>
  process.env.NEXT_PUBLIC_SELF_HOSTED_API_URL ?? 'http://localhost:4000';

function getToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('nexus_access_token');
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`[SelfHostedDB] ${path} → ${res.status}: ${msg}`);
  }
  return res.json() as Promise<T>;
}

function buildQuery(options?: QueryOptions): string {
  const params = new URLSearchParams();
  if (options?.filters) {
    options.filters.forEach((f, i) => {
      params.set(`filter[${i}][col]`, f.column);
      params.set(`filter[${i}][op]`, f.operator);
      params.set(`filter[${i}][val]`, String(f.value));
    });
  }
  if (options?.orderBy) {
    params.set('orderBy', options.orderBy.column);
    params.set('orderDir', options.orderBy.ascending === false ? 'desc' : 'asc');
  }
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export class SelfHostedDatabaseAdapter implements DatabaseAdapter {
  async select<T = Record<string, unknown>>(table: string, options?: QueryOptions): Promise<T[]> {
    return apiFetch<T[]>(`/db/${table}${buildQuery(options)}`);
  }

  async selectOne<T = Record<string, unknown>>(table: string, options?: QueryOptions): Promise<T | null> {
    const rows = await apiFetch<T[]>(`/db/${table}${buildQuery({ ...options, limit: 1 })}`);
    return rows[0] ?? null;
  }

  async insert<T = Record<string, unknown>>(table: string, rows: Partial<T> | Partial<T>[]): Promise<T[]> {
    return apiFetch<T[]>(`/db/${table}`, {
      method: 'POST',
      body: JSON.stringify(Array.isArray(rows) ? rows : [rows]),
    });
  }

  async update<T = Record<string, unknown>>(table: string, updates: Partial<T>, filters: QueryFilter[]): Promise<T[]> {
    return apiFetch<T[]>(`/db/${table}${buildQuery({ filters })}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async upsert<T = Record<string, unknown>>(table: string, rows: Partial<T> | Partial<T>[], options?: UpsertOptions): Promise<T[]> {
    const qs = options?.onConflict ? `?onConflict=${encodeURIComponent(options.onConflict)}` : '';
    return apiFetch<T[]>(`/db/${table}/upsert${qs}`, {
      method: 'PUT',
      body: JSON.stringify(Array.isArray(rows) ? rows : [rows]),
    });
  }

  async delete(table: string, filters: QueryFilter[]): Promise<boolean> {
    await apiFetch(`/db/${table}${buildQuery({ filters })}`, { method: 'DELETE' });
    return true;
  }

  async rpc<T = unknown>(fn: string, params?: Record<string, unknown>): Promise<T> {
    return apiFetch<T>(`/rpc/${fn}`, {
      method: 'POST',
      body: JSON.stringify(params ?? {}),
    });
  }
}
